import React, { useState, useCallback } from 'react';
import { stockDisponivel } from '../utils/stock';
import { useNavigate } from 'react-router-dom';
import { FiShoppingBag, FiPlus, FiMinus, FiEye, FiZap } from 'react-icons/fi';
import type { Product } from '../types';
import { useCart } from '../contexts/CartContext';
import { resolveImage } from '../utils/image';

interface ProductCardProps {
  product: Product;
  variant?: 'grid' | 'list' | 'compact';
  storeId?: string;
  onQuickView?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variant = 'grid',
  storeId,
  onQuickView,
}) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const handleViewDetail = () => {
    if (storeId) {
      navigate(`/ecommerce/store/${storeId}/product/${product.ID}`);
    } else if (onQuickView) {
      onQuickView(product);
    }
  };

  const imageSrc = resolveImage(product.IMAGEM, product.MIME_TYPE);

  const price = product.PRECO || 0;
  const hasPromotion =
    product.PRECO_PROMOCIONAL && product.PRECO_PROMOCIONAL < price;
  const discountPercent = hasPromotion
    ? Math.round((1 - product.PRECO_PROMOCIONAL! / price) * 100)
    : 0;

  const currentPrice = hasPromotion ? product.PRECO_PROMOCIONAL! : price;
  const isDigital = (product as any).TIPO === 'DIGITAL' || (product as any).tipo === 'DIGITAL';
  // A API manda `stock` minúsculo e como string ("0") — a leitura directa de
  // product.STOCK deixava esgotados parecer disponíveis. Ver utils/stock.ts.
  const stock = stockDisponivel(product);
  const isOutOfStock = !isDigital && stock <= 0;
  const isLowStock = !isDigital && Number.isFinite(stock) && stock > 0 && stock <= 5;

  const handleAddToCart = async () => {
    if (isOutOfStock) return;
    setIsAdding(true);
    await new Promise((resolve) => setTimeout(resolve, 300));
    const productWithStore = storeId ? { ...product, LOJA_ID: storeId } : product;
    addToCart(productWithStore, quantity);
    setQuantity(1);
    setIsAdding(false);
  };

  const incrementQuantity = () => {
    if (quantity < stock) {
      setQuantity((q) => q + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((q) => q - 1);
    }
  };

  const getStockIndicator = () => {
    if (isDigital) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
          <FiZap className="text-xs" />
          Entrega imediata
        </span>
      );
    }
    if (isOutOfStock) {
      return <span className="text-xs text-pagali-error">Esgotado</span>;
    }
    if (isLowStock) {
      return (
        <span className="text-xs text-pagali-warning">
          Apenas {stock} disponivel
        </span>
      );
    }
    return <span className="text-xs text-pagali-success">Em stock</span>;
  };

  // Compact variant for horizontal lists
  if (variant === 'compact') {
    return (
      <div
        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all cursor-pointer group"
        onClick={() => onQuickView?.(product)}
      >
        <div className="w-16 h-16 rounded-xl bg-pagali-gray-100 overflow-hidden flex-shrink-0">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.NOME}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiShoppingBag className="text-xl text-pagali-gray-400" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-pagali-gray-800 truncate text-sm">
            {product.NOME}
          </h4>
          <p className="text-pagali-primary font-semibold">
            {currentPrice.toLocaleString('pt-CV')} ECV
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            addToCart(product, 1);
          }}
          disabled={isOutOfStock}
          className="w-8 h-8 rounded-full bg-pagali-primary text-white flex items-center justify-center hover:bg-pagali-primary-dark transition-colors disabled:bg-pagali-gray-300 disabled:cursor-not-allowed"
        >
          <FiPlus className="text-sm" />
        </button>
      </div>
    );
  }

  // List variant
  if (variant === 'list') {
    return (
      <div className="flex gap-4 p-4 bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all group">
        <div className="relative w-24 h-24 rounded-xl bg-pagali-gray-100 overflow-hidden flex-shrink-0">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.NOME}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiShoppingBag className="text-2xl text-pagali-gray-400" />
            </div>
          )}
          {hasPromotion && (
            <span className="absolute top-1 right-1 bg-pagali-error text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              -{discountPercent}%
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-pagali-gray-800 truncate">
              {product.NOME}
            </h3>
            {product.DESCRICAO && (
              <p className="text-sm text-pagali-gray-500 truncate mt-0.5">
                {product.DESCRICAO}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-pagali-gray-900">
                {currentPrice.toLocaleString('pt-CV')} ECV
              </span>
              {hasPromotion && (
                <span className="text-sm text-pagali-gray-400 line-through">
                  {(product.PRECO || 0).toLocaleString('pt-CV')}
                </span>
              )}
            </div>
            {getStockIndicator()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onQuickView && (
            <button
              onClick={() => onQuickView(product)}
              className="w-10 h-10 rounded-full bg-pagali-gray-100 text-pagali-gray-600 flex items-center justify-center hover:bg-pagali-gray-200 transition-colors"
            >
              <FiEye />
            </button>
          )}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isAdding}
            className="px-4 py-2 rounded-full bg-pagali-purple text-white font-medium hover:bg-pagali-purple-light transition-colors disabled:bg-pagali-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isAdding ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <FiPlus />
                Adicionar
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Grid variant (default)
  return (
    <div className="bg-white rounded-2xl overflow-hidden group cursor-pointer border border-gray-100 hover:border-pagali-purple/30 hover:shadow-lg transition-all duration-200">
      {/* Image Container */}
      <div
        className="relative aspect-[3/4] bg-gray-100 overflow-hidden"
        onClick={handleViewDetail}
      >
        {/* Skeleton shimmer while loading */}
        {imageSrc && !imgLoaded && !imgError && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
        )}

        {imageSrc && !imgError ? (
          <img
            src={imageSrc}
            alt={product.NOME}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            className={`w-full h-full object-cover object-top transition-all duration-500
              ${imgLoaded ? 'opacity-100' : 'opacity-0'}
              group-hover:scale-[1.03]`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <FiShoppingBag className="text-4xl text-gray-300" />
          </div>
        )}

        {/* Gradient overlay at bottom for better text legibility */}
        {imageSrc && imgLoaded && (
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        )}

        {/* Badges */}
        {isDigital && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
            <FiZap className="text-[10px]" />DIGITAL
          </span>
        )}
        {hasPromotion && (
          <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
            -{discountPercent}%
          </span>
        )}

        {/* Price overlay on image */}
        {imageSrc && imgLoaded && (
          <span className="absolute bottom-2 left-2 text-white text-sm font-bold drop-shadow-md">
            {currentPrice.toLocaleString('pt-CV')}$
          </span>
        )}

        {/* Quick View on hover */}
        <button
          onClick={(e) => { e.stopPropagation(); handleViewDetail(); }}
          className="absolute inset-x-3 bottom-8 py-2 bg-white/95 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-xl opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-200 flex items-center justify-center gap-1.5 shadow-lg"
        >
          <FiEye className="text-xs" />Ver produto
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Name and Description */}
        <h3
          className="font-semibold text-gray-900 leading-snug line-clamp-2 cursor-pointer hover:text-pagali-purple transition-colors text-sm"
          onClick={handleViewDetail}
        >
          {product.NOME}
        </h3>
        {product.DESCRICAO && (
          <p className="text-xs text-gray-400 truncate mt-0.5">
            {product.DESCRICAO}
          </p>
        )}

        {/* Price + Stock */}
        <div className="mt-2 flex items-center justify-between gap-1">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-sm font-bold text-gray-900 truncate">
              {currentPrice.toLocaleString('pt-CV')}$
            </span>
            {hasPromotion && (
              <span className="text-xs text-gray-400 line-through">
                {(product.PRECO || 0).toLocaleString('pt-CV')}$
              </span>
            )}
          </div>
          <div className="flex-shrink-0">{getStockIndicator()}</div>
        </div>

        {/* Add to Cart */}
        <div className="mt-3 flex items-center gap-1.5">
          {!isDigital && (
            <div className="flex items-center bg-gray-100 rounded-full p-0.5">
              <button
                onClick={decrementQuantity}
                disabled={quantity <= 1 || isOutOfStock}
                className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              >
                <FiMinus className="text-xs" />
              </button>
              <span className="w-5 text-center text-xs font-semibold text-gray-800">{quantity}</span>
              <button
                onClick={incrementQuantity}
                disabled={quantity >= stock || isOutOfStock}
                className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              >
                <FiPlus className="text-xs" />
              </button>
            </div>
          )}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isAdding}
            className="flex-1 py-2 rounded-xl bg-gray-900 text-white text-xs font-semibold hover:bg-gray-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1"
          >
            {isAdding ? (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isOutOfStock ? 'Esgotado' : isDigital ? (
              <><FiZap className="text-xs" />Comprar</>
            ) : 'Adicionar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
